import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { OrgsService } from "@app2/account/orgs.service";
import { combineLatest, Observable } from "rxjs";
import { PeopleService } from "@app2/account/people.service";
import { PersonSummary } from "@app2/type-defs/user/user-types";
import { filter, map } from "rxjs/operators";
import { DsSelectOption } from "@app2/shared/components/ds-select.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "person-picker",
    template: `
        <ds-select test-id="person-picker"
                   [multiple]="multiple"
                   [shortLabel]="shortLabel"
                   [selectedOption]="multiple ? selectedIds : selectedId"
                   [options]="allUserOptions"
                   [placeholderKey]="placeholder"
                   [savable]="savable"
                   (selectedOptionChange)="userIdChanged($event)"
                   (selectedOptionsChange)="userIdsChanged($event)">
        </ds-select>
    `,
})
export class PersonPickerComponent implements OnChanges, OnInit {
    @Input() selectedId: uuid;
    @Input() selectedIds: uuid[];
    @Input() placeholder: string;
    @Input() multiple: boolean;
    @Input() shortLabel: boolean;
    @Input() homeOrgOnly: boolean = false;
    @Input() clearOnSelect: boolean = false;
    @Input() excludedUsers: uuid[] = [];
    @Input() savable: boolean;
    @Output() selectedIdChange = new EventEmitter<uuid>();
    @Output() selectedIdsChange = new EventEmitter<uuid[]>();

    allUsers: PersonSummary[] = [];
    allUserOptions: DsSelectOption<uuid>[] = [];

    constructor(private peopleService: PeopleService,
                private orgService: OrgsService) {
    }

    ngOnInit() {
        let people$: Observable<PersonSummary[]>;
        if (this.homeOrgOnly) {
            people$ = combineLatest([
                this.peopleService.getActiveUsersWithAccessToCurrentOrg$(false),
                this.orgService.getCurrentOrg$().pipe(filter(org => !!org)),
            ])
                .pipe(
                    map(([allUsers, currentOrg]) =>
                        allUsers.filter(user => user.homeOrgId === currentOrg.id)),
                );
        } else {
            people$ = this.peopleService.getActiveUsersWithAccessToCurrentOrg$(false);
        }

        people$
            .pipe(untilDestroyed(this))
            .subscribe(users => {
                this.allUsers = users;
                this.updateOptions();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.excludedUsers) {
            this.updateOptions();
        }
    }

    userIdChanged(id: uuid) {
        this.selectedId = id;
        this.selectedIdChange.emit(this.selectedId);
        if (this.clearOnSelect) {
            this.selectedId = null;
        }
    }

    userIdsChanged(ids: uuid[]) {
        this.selectedIds = ids;
        this.selectedIdsChange.emit(this.selectedIds);
        if (this.clearOnSelect) {
            this.selectedIds = null;
        }
    }

    private updateOptions(): void {
        this.allUserOptions = this.allUsers
            .filter(user => !this.excludedUsers.includes(user.id))
            .map(user => ({ key: user.fullName, value: user.id }));
    }
}
